import { Group, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import type { FC } from 'react';

import { formatDate } from '../../../lib/formatDate.ts';
import { formatTime } from '../../../lib/formatTime.ts';
import { generateRecurrences } from '../../../lib/generateRecurrences.ts';

interface EventDatesProps {
  start?: Date;
  end?: Date;
  recurrenceActive?: boolean;
  recurrenceRate?: 'weekly' | 'bi-weekly' | 'monthly';
  recurrenceAmount?: number | null;
  recurrenceMonthType?: string | null;
}

export const EventDates: FC<EventDatesProps> = ({
  start,
  end,
  recurrenceActive,
  recurrenceRate,
  recurrenceAmount,
  recurrenceMonthType,
}) => {
  // Recurrence summary mode
  if (recurrenceActive && start && recurrenceRate && recurrenceAmount) {
    const dates = generateRecurrences({
      date: start,
      noOfEvents: recurrenceAmount,
      recurrenceRate,
      monthRecurrenceType: recurrenceMonthType ?? null,
    });

    if (dates.length === 0) {
      return null;
    }

    const [first] = dates;
    const last = dates.at(-1);
    if (!first || !last) {
      return null;
    }

    const weekday = dayjs(start).format('dddd');
    const firstDate = formatDate(first);
    const lastDate = formatDate(last);

    return (
      <Stack gap={2}>
        <Text c="gigi-grey-text" fw="600" size="xxs">
          Date and Time:
        </Text>
        <Text size="sm">
          Every {weekday} &middot; {dates.length} events &middot; {firstDate} – {lastDate}
        </Text>
        {start && (
          <Group gap="xs">
            <Text size="xs" w={46}>
              Time:
            </Text>
            <Text size="sm">{formatTime(start)}</Text>
            {end && (
              <>
                <Text size="xs">–</Text>
                <Text size="sm">{formatTime(end)}</Text>
              </>
            )}
          </Group>
        )}
      </Stack>
    );
  }

  // Default single-date mode
  return (
    <Stack gap={2}>
      <Text c="gigi-grey-text" fw="600" size="xxs">
        Date and Time:
      </Text>
      <Stack gap={2}>
        {start && (
          <Group gap="xs">
            <Text size="xs" w={46}>
              Starts:
            </Text>
            <Text size="sm">{formatDate(start)}</Text>
            <Text size="sm">{formatTime(start)}</Text>
          </Group>
        )}
        {end && (
          <Group gap="xs">
            <Text size="xs" w={46}>
              Ends:
            </Text>
            <Text size="sm">{formatDate(end)}</Text>
            <Text size="sm">{formatTime(end)}</Text>
          </Group>
        )}
      </Stack>
    </Stack>
  );
};
